// src/components/Assets/AssetForm.js
// Modal form for creating and editing assets

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import "./style.css";

const AssetForm = ({ asset, onSubmit, onCancel, isSubmitting }) => {
  const [t] = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    details: ""
  });
  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name || "",
        details: asset.details || ""
      });
    }
  }, [asset]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("Name is required");
    }

    if (!formData.details.trim()) {
      newErrors.details = t("Details is required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{asset ? t("Edit Asset") : t("Add New Asset")}</h2>
          <button className="close-btn" onClick={onCancel} type="button">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="asset-form">
          {/* Name Field */}
          <div className="form-group">
            <label htmlFor="name">
              {t("Name")} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t("Enter asset name")}
              className={errors.name ? "input-error" : ""}
              disabled={isSubmitting}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          {/* Details Field */}
          <div className="form-group">
            <label htmlFor="details">
              {t("Details")} <span className="required">*</span>
            </label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              placeholder={t("Enter asset details")}
              rows={4}
              className={errors.details ? "input-error" : ""}
              disabled={isSubmitting}
            />
            {errors.details && <span className="error-message">{errors.details}</span>}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes validation
AssetForm.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    details: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool
};

// Default props
AssetForm.defaultProps = {
  asset: null,
  isSubmitting: false
};

export default AssetForm;
